import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu
 * @property {HTMLElement[]} [submenu]
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * Delay timer before closing the desktop mega menu.
   * @type {number | null}
   */
  #closeTimer = null;

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener(
      'resize',
      this.#resizeListener
    );

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    this.#cleanupMutationObserver();
  }

  /**
   * Debounced resize event listener
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Overflow submenu listener
   */
  #overflowSubmenuListener = () => {
    this.#scheduleDeactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector(
        '[part="overflow"]'
      )
    );
  }

  /**
   * Whether the overflow list is hovered
   */
  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (
      this.closest('header-component')
    );
  }

  /**
   * Cancel pending menu close
   */
  #cancelClose = () => {
    if (this.#closeTimer) {
      clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  };

  /**
   * Schedule menu close with delay
   */
  #scheduleDeactivate = () => {
    this.#cancelClose();

    this.#closeTimer = setTimeout(() => {
      this.#deactivate();
      this.#closeTimer = null;
    }, 650);
  };

  /**
   * Activate selected menu item
   *
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#cancelClose();

    this.dispatchEvent(
      new MegaMenuHoverEvent()
    );

    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    let item = findMenuItem(
      event.target
    );

    if (
      !item ||
      item === this.#state.activeItem
    ) {
      return;
    }

    const isDefaultSlot =
      event.target.slot === '';

    this.dataset.overflowExpanded =
      (!isDefaultSlot).toString();

    const previouslyActiveItem =
      this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded =
        'false';
    }

    this.#state.activeItem = item;

    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    const hasSubmenu =
      Boolean(submenu);

    if (
      !hasSubmenu &&
      !isDefaultSlot
    ) {
      submenu =
        this.overflowMenu;
    }

    if (submenu) {
      submenu.dataset.active = '';

      this.#cleanupMutationObserver();

      this.#submenuMutationObserver =
        new MutationObserver(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (
                submenu.offsetHeight > 0
              ) {
                this.headerComponent?.style.setProperty(
                  '--submenu-height',
                  `${submenu.offsetHeight}px`
                );

                this.#cleanupMutationObserver();
              }
            });
          });
        });

      this.#submenuMutationObserver.observe(
        submenu,
        {
          childList: true,
          subtree: true,
        }
      );

      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight =
      submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight =
        this.#getOverflowListLinksHeight();

      if (hasSubmenu) {
        const overflowHeight =
          this.overflowMenu?.offsetHeight || 0;

        finalHeight = Math.max(
          overflowHeight,
          overflowListHeight
        );
      } else {
        finalHeight =
          overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    this.headerComponent.style.setProperty(
      '--submenu-height',
      `${finalHeight}px`
    );

    this.#setFullOpenHeaderHeight(
      finalHeight
    );

    this.style.setProperty(
      '--submenu-opacity',
      '1'
    );
  };

  /**
   * Deactivate active item after delay
   *
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (
      !(event.target instanceof Element)
    ) {
      return;
    }

    const menu = findSubmenu(
      this.#state.activeItem
    );

    const relatedTarget =
      event.relatedTarget;

    /**
     * If pointer/focus is moving into
     * the submenu, keep it open.
     */
    const isMovingToSubmenu =
      relatedTarget instanceof Node &&
      menu?.contains(relatedTarget);

    /**
     * If pointer/focus remains anywhere
     * inside the current menu item,
     * keep it open.
     */
    const activeItem =
      this.#state.activeItem;

    const isMovingWithinActiveItem =
      relatedTarget instanceof Node &&
      activeItem?.parentElement?.contains(
        relatedTarget
      );

    /**
     * Keep overflow menu open while
     * pointer moves into overflow items.
     */
    const isMovingToOverflowMenu =
      relatedTarget instanceof Node &&
      (
        relatedTarget.parentElement?.matches(
          '[slot="overflow"]'
        ) ||
        this.overflowMenu?.contains(
          relatedTarget
        )
      );

    if (
      isMovingToSubmenu ||
      isMovingWithinActiveItem ||
      isMovingToOverflowMenu
    ) {
      this.#cancelClose();
      return;
    }

    this.#scheduleDeactivate();
  }

  /**
   * Deactivate active item immediately
   *
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (
    item = this.#state.activeItem
  ) => {
    if (
      !item ||
      item !== this.#state.activeItem
    ) {
      return;
    }

    /**
     * Do not deactivate if overflow
     * menu is still hovered.
     */
    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    /**
     * Extra safeguard:
     * if submenu itself is still hovered,
     * do not close it.
     */
    const submenu =
      findSubmenu(item);

    if (
      submenu?.matches(':hover')
    ) {
      return;
    }

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.#setFullOpenHeaderHeight(
      0
    );

    this.style.setProperty(
      '--submenu-opacity',
      '0'
    );

    this.dataset.overflowExpanded =
      'false';

    this.#state.activeItem =
      null;

    this.ariaExpanded =
      'false';

    item.ariaExpanded =
      'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Calculate overflow menu height
   */
  #getOverflowListLinksHeight() {
    const slottedMenuLinks =
      this.overflowMenu
        ?.querySelector('slot')
        ?.assignedElements();

    if (!slottedMenuLinks) {
      return (
        this.overflowMenu?.offsetHeight ||
        0
      );
    }

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach(
        (link) => {
          const submenu =
            /** @type {HTMLElement | null} */ (
              link.querySelector(
                '[ref="submenu[]"]'
              )
            );

          if (submenu) {
            cb(submenu);
          }
        }
      );
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty(
        'display',
        'none'
      );
    });

    const height =
      this.overflowMenu?.offsetHeight ||
      0;

    mapSubmenus((submenu) => {
      submenu.style.removeProperty(
        'display'
      );
    });

    return height;
  }

  /**
   * Calculate and set full open header height
   *
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(
    submenuHeight
  ) {
    if (!this.headerComponent) {
      return;
    }

    const isOverlapSituation =
      this.headerComponent.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const headerVisibleHeight =
      isOverlapSituation &&
      this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (
            this.headerComponent.querySelector(
              '.header__row--top'
            )
          )?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen =
      submenuHeight === 0;

    const fullOpenHeaderHeight =
      nothingToOpen
        ? 0
        : submenuHeight +
          (headerVisibleHeight ?? 0);

    this.headerComponent.style.setProperty(
      '--full-open-header-height',
      `${fullOpenHeaderHeight}px`
    );
  }

  /**
   * Preload menu images
   */
  #preloadImages = () => {
    const images =
      this.querySelectorAll(
        'img[loading="lazy"]'
      );

    images?.forEach((image) =>
      image.removeAttribute(
        'loading'
      )
    );
  };

  /**
   * Cleanup submenu observer
   */
  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();

    this.#submenuMutationObserver =
      null;
  }
}

if (
  !customElements.get(
    'header-menu'
  )
) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

/**
 * Find closest menu item
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (
    !(element instanceof Element)
  ) {
    return null;
  }

  if (
    element?.matches(
      '[slot="more"'
    )
  ) {
    return findMenuItem(
      element.parentElement?.querySelector(
        '[slot="overflow"]'
      )
    );
  }

  return element?.querySelector(
    '[ref="menuitem"]'
  );
}

/**
 * Find closest submenu
 *
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu =
    element?.parentElement?.querySelector(
      '[ref="submenu[]"]'
    );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}